/**
 * F-404-FE-02: ScrollGateButton
 *
 * IntersectionObserver monitors scroll position within the note content area.
 * - Disabled state: "Review to Complete ↓"
 * - On scroll-to-bottom: fires PATCH /api/notes/:id/scroll-confirmed, enables button
 * - Enabled state: "Mark as Complete"
 * - On click: calls POST /api/notes/:id/complete
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { api, ApiError } from '@/lib/apiClient';

interface ScrollGateButtonProps {
  noteId: string;
  /** Reference to the sentinel element at the bottom of the note content area */
  sentinelRef: React.RefObject<HTMLDivElement | null>;
  /** Called after completion API succeeds */
  onComplete: () => void;
  /** True if the backend already recorded scroll confirmation (e.g. re-open) */
  initialScrolled?: boolean;
  disabled?: boolean;
}

export function ScrollGateButton({
  noteId,
  sentinelRef,
  onComplete,
  initialScrolled = false,
  disabled = false,
}: ScrollGateButtonProps) {
  const [scrollConfirmed, setScrollConfirmed] = useState(initialScrolled);
  const [confirmingScroll, setConfirmingScroll] = useState(false);
  const [completing, setCompleting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showConfirm, setShowConfirm] = useState(false);
  const hasConfirmedRef = useRef(initialScrolled);

  // IntersectionObserver: watch sentinel element at bottom of content
  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel || hasConfirmedRef.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry?.isIntersecting && !hasConfirmedRef.current) {
          hasConfirmedRef.current = true;
          handleScrollConfirm();
        }
      },
      {
        // Fire when sentinel is at least 10% visible
        threshold: 0.1,
      },
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [noteId, sentinelRef]);

  const handleScrollConfirm = useCallback(async () => {
    if (scrollConfirmed || confirmingScroll) return;
    setConfirmingScroll(true);
    try {
      await api.patch(`/notes/${noteId}/scroll-confirmed`);
      setScrollConfirmed(true);
    } catch (err) {
      // Non-fatal — allow completion attempt even if this call fails
      console.warn('scroll-confirmed call failed:', err);
      setScrollConfirmed(true);
    } finally {
      setConfirmingScroll(false);
    }
  }, [noteId, scrollConfirmed, confirmingScroll]);

  const handleCompleteClick = useCallback(() => {
    setShowConfirm(true);
    setError(null);
  }, []);

  const handleConfirmComplete = useCallback(async () => {
    setCompleting(true);
    setError(null);
    setShowConfirm(false);
    try {
      await api.post(`/notes/${noteId}/complete`);
      onComplete();
    } catch (err) {
      const message =
        err instanceof ApiError ? err.message : 'Failed to complete note.';
      setError(message);
    } finally {
      setCompleting(false);
    }
  }, [noteId, onComplete]);

  const isEnabled = scrollConfirmed && !disabled && !completing;

  return (
    <div className="flex flex-col items-stretch gap-3 pt-6 border-t border-clinical-border">
      {error && (
        <p className="text-sm text-safety-red text-center">{error}</p>
      )}

      {/* Confirmation prompt */}
      {showConfirm && (
        <div className="clinical-card p-4 flex flex-col gap-3">
          <p className="text-sm font-body text-clinical-charcoal text-center">
            Mark this note as complete? This action cannot be undone.
          </p>
          <div className="flex gap-3 justify-center">
            <button
              type="button"
              className="btn-secondary px-5 py-2 text-sm"
              onClick={() => setShowConfirm(false)}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn-primary px-5 py-2 text-sm min-h-[40px]"
              onClick={handleConfirmComplete}
            >
              Yes, mark complete
            </button>
          </div>
        </div>
      )}

      {/* Main gate button */}
      <button
        type="button"
        className={[
          'w-full flex items-center justify-center gap-2 rounded-clinical font-body font-semibold',
          'transition-all duration-200 min-h-[56px] text-base',
          isEnabled
            ? 'bg-clinical-teal text-white hover:bg-clinical-teal-dark shadow-clinical-md'
            : 'bg-clinical-cream-dark text-clinical-text-muted cursor-not-allowed',
        ].join(' ')}
        disabled={!isEnabled}
        onClick={handleCompleteClick}
        aria-label={
          isEnabled ? 'Mark note as complete' : 'Scroll to bottom to continue'
        }
      >
        {completing ? (
          <>
            <SpinnerIcon />
            Completing...
          </>
        ) : isEnabled ? (
          <>
            <CheckIcon />
            Mark as Complete
          </>
        ) : (
          <>
            {confirmingScroll ? (
              <SpinnerIcon />
            ) : (
              <ArrowDownIcon />
            )}
            Review to Complete{confirmingScroll ? '...' : ' ↓'}
          </>
        )}
      </button>

      {!scrollConfirmed && !confirmingScroll && (
        <p className="text-xs text-clinical-text-muted text-center font-body">
          Scroll to the bottom of the note to enable completion
        </p>
      )}
    </div>
  );
}

// --- Small inline icons ---

function CheckIcon() {
  return (
    <svg
      className="w-5 h-5"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2.5}
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M4.5 12.75l6 6 9-13.5"
      />
    </svg>
  );
}

function ArrowDownIcon() {
  return (
    <svg
      className="w-4 h-4"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M19 9l-7 7-7-7"
      />
    </svg>
  );
}

function SpinnerIcon() {
  return (
    <svg
      className="w-4 h-4 animate-spin"
      fill="none"
      viewBox="0 0 24 24"
      aria-hidden="true"
    >
      <circle
        className="opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="4"
      />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
      />
    </svg>
  );
}
